/**
 * ResolvedIssuesPanel - station retry feed for resolved print-file issues.
 *
 * A print file that was missing (garment blocked at the printer) gets resolved
 * when artwork is uploaded/rechecked, auto-healed by the bridge, or healed from
 * a designs folder. This panel surfaces those freshly-resolved garments so the
 * printer can tap one to reload it and reprint — no walking back to a desk.
 *
 * Production-app surface (PRODUCTION_DESIGN.md): chrome-free, semantic colour
 * (emerald = ready/completed), ≥48px hit targets, keyboard/scanner-safe. Zero
 * chrome when empty — the strip only appears once there's something to retry.
 *
 * Self-contained: owns its query (useResolvedIssues), its live invalidation
 * (useResolvedIssueEvents), and the shared dismiss mutation. A dismiss is
 * server-stored and shared across every operator's panel at the location.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { encodeGarmentQr } from '@/lib/qr-utils';
import { useResolvedIssues } from '../hooks/useResolvedIssues';
import { useResolvedIssueEvents } from '../hooks/useResolvedIssueEvents';
import { useFormatTimeAgo } from '../hooks/useFormatTimeAgo';

/** Contract shape from `printStation.getRecentlyResolvedIssues` (backend PR). */
interface ResolvedIssueItem {
  issueId: string;
  prodOrderId: string;
  shopifySku: string;
  orderName: string | null;
  productName: string | null;
  size: string | null;
  color: string | null;
  variantTitle: string | null;
  garmentUnitId: string | null;
  resolvedAt: string;
  /**
   * Which tab this entry belongs to (server-derived one-way ratchet from the
   * issue's filed-notification state): 'self_heal' = fixed silently before
   * anyone was notified or tried to print; 'manual' = operator-known
   * (manually filed, or auto-fetch failed and Slack fired before the fix).
   * The tab partition below still routes anything that isn't exactly
   * 'self_heal' to Manual, so a stale-cached row without the field lands on
   * the safe side — never hide something an operator may have known about.
   */
  category: 'manual' | 'self_heal';
}

type FeedTab = 'manual' | 'self_heal';

interface Props {
  locationId: string | null;
  onLoadGarment: (qr: string) => void;
  /**
   * When a garment is loaded / printing, the operator is mid-task and the
   * expandable list must not cover the active print surface. While true the
   * panel force-collapses to the strip only (§ PRODUCTION_DESIGN.md: the current
   * task fills the screen). The strip itself stays visible as a "ready to retry"
   * cue.
   */
  hasActiveGarment: boolean;
  /**
   * Fully hide the panel (render nothing) while a garment is loading or loaded
   * on the printing screen — the print flow owns the screen and the retry
   * banner must not compete with it. The component stays MOUNTED (the SSE feed
   * + poll keep running) so toggling this never reconnects the capped station
   * SSE pool.
   */
  hidden?: boolean;
}

export function ResolvedIssuesPanel({ locationId, onLoadGarment, hasActiveGarment, hidden = false }: Props) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const formatTimeAgo = useFormatTimeAgo();
  const [expanded, setExpanded] = useState(false);
  // Manual is ALWAYS the default view — operator-relevant entries first; the
  // Self-Healing tab is the audit trail of silent end-to-end auto-heals.
  const [tab, setTab] = useState<FeedTab>('manual');
  const listId = useId();
  const stripRef = useRef<HTMLButtonElement>(null);

  const { data } = useResolvedIssues(locationId);
  // Live feed: SSE invalidates the query on a resolve/heal at this location.
  useResolvedIssueEvents(locationId);

  // A loaded garment fills the screen — collapse the list so it never covers the
  // active print task. Stored state is reset (not merely hidden) so clearing the
  // garment leaves the panel collapsed rather than snapping back open.
  useEffect(() => {
    if (hasActiveGarment) setExpanded(false);
  }, [hasActiveGarment]);

  const items: ResolvedIssueItem[] = data?.items ?? [];
  // Tab partition. Missing category (pre-category cache/server) → Manual —
  // safer: never hide something an operator may have known about.
  const manualItems = items.filter((i) => i.category !== 'self_heal');
  const selfHealItems = items.filter((i) => i.category === 'self_heal');
  const tabItems = tab === 'manual' ? manualItems : selfHealItems;
  // The list may only open when no garment is active.
  const isExpanded = expanded && !hasActiveGarment;

  const dismiss = trpc.printStation.dismissResolvedIssue.useMutation({
    // Optimistic removal — the row leaves instantly; roll back on failure.
    onMutate: async (vars: { issueId: string }) => {
      if (!locationId) return { prev: undefined };
      const key = { locationId };
      await utils.printStation.getRecentlyResolvedIssues.cancel(key);
      const prev = utils.printStation.getRecentlyResolvedIssues.getData(key);
      utils.printStation.getRecentlyResolvedIssues.setData(
        key,
        (old: { items: ResolvedIssueItem[] } | undefined) =>
          old ? { items: old.items.filter((i) => i.issueId !== vars.issueId) } : old,
      );
      return { prev };
    },
    onError: (_err: unknown, _vars: unknown, ctx: { prev?: { items: ResolvedIssueItem[] } } | undefined) => {
      if (locationId && ctx?.prev) {
        utils.printStation.getRecentlyResolvedIssues.setData({ locationId }, ctx.prev);
      }
      void utils.printStation.getRecentlyResolvedIssues.invalidate();
      // Non-blocking secondary action — a toast (repo precedent on this page for
      // report-issue failures) rather than an operator-blocking scan modal.
      toast.error(t('printer.resolvedIssues.dismissFailed'));
    },
  });

  // Zero chrome when there's nothing to retry (or before a location is chosen),
  // or while a garment is loading/loaded on the printing screen (`hidden`).
  // Hooks above run unconditionally so the SSE + poll keep the feed live even
  // while the panel renders nothing.
  if (!locationId || items.length === 0 || hidden) return null;

  return (
    <div className="sticky top-0 z-10 border-b bg-background">
      {/* Collapsed strip — full-width toggle, semantic emerald "ready" state.
          Disabled (non-expanding) while a garment is active: it stays visible as
          a "ready to retry" cue but must not open over the active print task. */}
      <button
        ref={stripRef}
        type="button"
        onClick={() => setExpanded((v) => !v)}
        disabled={hasActiveGarment}
        aria-expanded={isExpanded}
        aria-controls={listId}
        className="flex h-14 w-full items-center gap-3 bg-emerald-50 px-4 text-left transition-colors hover:bg-emerald-100 disabled:cursor-default disabled:hover:bg-emerald-50 motion-reduce:transition-none dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50 dark:disabled:hover:bg-emerald-950/30"
      >
        <CheckCircle2
          className="h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-400"
          aria-hidden="true"
        />
        <span className="flex-1 text-base font-semibold text-emerald-800 dark:text-emerald-200">
          {t('printer.resolvedIssues.banner', { count: items.length })}
        </span>
        {/* Only offer the expand chevron when the list can actually open. */}
        {!hasActiveGarment &&
          (isExpanded ? (
            <ChevronUp className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          ))}
      </button>

      {/* Expanded region — Manual (default) / Self-Healing tabs, then a
          scrollable list of the active tab's rows. Manual = entries an
          operator knew about (filed, or auto-fetch failed → Slack fired);
          Self-Healing = the audit trail of silent end-to-end auto-heals. */}
      {isExpanded && (
        <div id={listId}>
          <div role="tablist" aria-label={t('printer.resolvedIssues.tabsLabel')} className="flex border-b bg-background">
            {(
              [
                { key: 'manual' as const, label: t('printer.resolvedIssues.tabManual', { count: manualItems.length }) },
                { key: 'self_heal' as const, label: t('printer.resolvedIssues.tabSelfHeal', { count: selfHealItems.length }) },
              ]
            ).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={cn(
                  'flex h-12 flex-1 items-center justify-center border-b-2 px-4 text-sm font-semibold transition-colors motion-reduce:transition-none',
                  tab === key
                    ? 'border-emerald-600 text-emerald-800 dark:border-emerald-400 dark:text-emerald-200'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {tabItems.length === 0 ? (
            <p className="bg-background px-4 py-6 text-center text-sm text-muted-foreground">
              {t('printer.resolvedIssues.tabEmpty')}
            </p>
          ) : (
        <ul className="max-h-[60vh] divide-y overflow-y-auto bg-background">
          {tabItems.map((item) => {
            const canLoad = !!item.garmentUnitId;
            return (
              <li key={item.issueId} className="flex items-stretch">
                {/* Load region — tap to reload the garment for a reprint. */}
                <button
                  type="button"
                  disabled={!canLoad}
                  onClick={() => {
                    if (!item.garmentUnitId) return;
                    // Mirror the Recently-Printed click exactly: legacy garment
                    // QR shape ({ garment_unit_id }) the lookup accepts.
                    onLoadGarment(encodeGarmentQr(item.garmentUnitId));
                  }}
                  className={cn(
                    'flex min-h-[48px] flex-1 flex-col gap-1 px-4 py-2.5 text-left transition-colors motion-reduce:transition-none',
                    canLoad ? 'hover:bg-muted/50' : 'cursor-not-allowed opacity-60',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold">
                      {item.orderName ?? t('printer.resolvedIssues.unknownOrder')}
                    </span>
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                      {formatTimeAgo(item.resolvedAt)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-sm text-muted-foreground">
                      {item.shopifySku}
                    </span>
                    {item.productName && (
                      <span className="min-w-0 truncate text-sm text-muted-foreground">
                        {item.productName}
                      </span>
                    )}
                    {item.size && (
                      <Badge variant="secondary" className="text-xs">
                        {item.size}
                      </Badge>
                    )}
                    {item.color && (
                      <Badge variant="secondary" className="text-xs">
                        {item.color}
                      </Badge>
                    )}
                  </div>
                  {!canLoad && (
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      {item.orderName
                        ? t('printer.resolvedIssues.scanHint', { order: item.orderName })
                        : t('printer.resolvedIssues.scanHintGeneric')}
                    </span>
                  )}
                </button>

                {/* Dismiss — sibling button (never nested), shared per-location.
                    The server resolves prodOrderId/shopifySku from the issue row,
                    so the client sends only the identity + location. */}
                <button
                  type="button"
                  onClick={() => {
                    // Move focus to the strip toggle BEFORE the optimistic remove
                    // unmounts this button — keyboard/SR focus lands somewhere
                    // predictable instead of dropping to <body>.
                    stripRef.current?.focus();
                    dismiss.mutate({ issueId: item.issueId, locationId });
                  }}
                  aria-label={t('printer.resolvedIssues.dismiss')}
                  className="flex h-12 w-12 shrink-0 items-center justify-center self-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground motion-reduce:transition-none"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
          )}
        </div>
      )}
    </div>
  );
}
